<script lang="ts" setup>
  import type { IconTypes, Maybe } from '@/types'

  import { computed } from 'vue'

  import resolveConfig from 'tailwindcss/resolveConfig'
  import tailwindConfig from '@/../tailwind.config'

  const props = withDefaults(
    defineProps<{
      icon: Maybe<IconTypes>
      color?: string | string[]
      hover?: boolean
    }>(),
    {
      color: 'black',
      hover: false,
    }
  )

  const fullConfig = resolveConfig(tailwindConfig)
  const colors = fullConfig.theme?.colors ?? {}

  const color = computed(() => {
    const index = +props.hover

    const c = Array.isArray(props.color) ? props.color[index] : props.color

    return (
      c.startsWith('m') || c.startsWith('c')
        ? colors[c as keyof typeof colors] ?? 'black'
        : c
    ) as string
  })
</script>

<template>
  <span
    v-if="props.icon === 'plus'"
    :class="`bg-${color}`"
    class="icon icon--plus"
  >
    <span />
  </span>

  <span
    v-else-if="props.icon === 'minus'"
    :class="`bg-${color}`"
    class="icon icon--minus"
  >
    <span />
  </span>

  <span
    v-else-if="props.icon === 'plus-medium'"
    :class="`bg-${color}`"
    class="icon icon--plus icon--plus-medium"
  >
    <span />
  </span>

  <span
    v-else-if="props.icon === 'plus-small'"
    :class="`bg-${color}`"
    class="icon icon--plus icon--plus-small"
  >
    <span />
  </span>

  <span v-else-if="props.icon === 'trash'" class="icon icon--trash">
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4 6V14H7.99989H12V6M13 4H10H6H3M8 2V4"
        :stroke="color"
        stroke-width="1.2"
      />
    </svg>
  </span>

  <span
    v-else-if="props.icon === 'trash-red'"
    class="icon icon--trash icon--trash-red"
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4 6V14H7.99989H12V6M13 4H10H6H3M8 2V4"
        stroke="#fe5d2a"
        stroke-width="1.2"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'tune'" class="icon icon--tune">
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3.5 12H1M15 12H8.5M6 10V14M8.5 8H1M15 8H13.5M11 6V10M3.5 4H1M15 4H8.5M6 2V6"
        :stroke="color"
        stroke-width="1.2"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'activate'" class="icon icon--activate">
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M4.00059 1.40002C3.11695 1.40002 2.40061 2.11635 2.40059 2.99999L2.40039 13C2.40037 13.8837 3.11672 14.6 4.00039 14.6H6.00037V13.4H4.00039C3.77947 13.4 3.60039 13.2209 3.60039 13L3.60059 3.00002C3.6006 2.77911 3.77968 2.60002 4.00059 2.60002H12.0006C12.2215 2.60002 12.4006 2.77911 12.4006 3.00002V7.00002H13.6006V3.00002C13.6006 2.11637 12.8843 1.40002 12.0006 1.40002H4.00059ZM13.5762 8.57574L9.00046 13.1515L6.42472 10.5757L5.57619 11.4243L8.57619 14.4243L9.00046 14.8485L9.42472 14.4243L14.4247 9.42426L13.5762 8.57574Z"
        :fill="color"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'deactivate'" class="icon icon--deactivate">
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M2.40059 2.99996C2.40061 2.11632 3.11695 1.39999 4.00059 1.39999H12.0006C12.8843 1.39999 13.6006 2.11634 13.6006 2.99999V6.99999H12.4006V2.99999C12.4006 2.77908 12.2215 2.59999 12.0006 2.59999H4.00059C3.77968 2.59999 3.6006 2.77908 3.60059 2.99999L3.60039 13C3.60039 13.2209 3.77947 13.4 4.00039 13.4H6.00037V14.6H4.00039C3.11672 14.6 2.40037 13.8836 2.40039 13L2.40059 2.99996ZM11.0005 11.1514L8.42472 8.57571L7.57619 9.42423L10.1519 12L7.57619 14.5757L8.42472 15.4242L11.0005 12.8485L13.5762 15.4242L14.4247 14.5757L11.849 12L14.4247 9.42423L13.5762 8.57571L11.0005 11.1514Z"
        :fill="color"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'eye'" class="icon icon--eye">
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M12 17.4C14.6301 17.4 17.3828 15.699 20.2457 12C17.3828 8.30106 14.6301 6.6 12 6.6C9.36986 6.6 6.61723 8.30106 3.75425 12C6.61723 15.699 9.36986 17.4 12 17.4ZM2.52 11.64C5.55846 7.58872 8.71402 5.4 12 5.4C15.286 5.4 18.4415 7.58872 21.48 11.64L21.75 12L21.48 12.36C18.4415 16.4113 15.286 18.6 12 18.6C8.71402 18.6 5.55846 16.4113 2.52 12.36L2.25 12L2.52 11.64ZM13.4 12C13.4 11.2268 12.7732 10.6 12 10.6C11.2268 10.6 10.6 11.2268 10.6 12C10.6 12.7732 11.2268 13.4 12 13.4C12.7732 13.4 13.4 12.7732 13.4 12ZM11.4 9.4V9.46957C10.2533 9.74044 9.4 10.7706 9.4 12C9.4 13.4359 10.5641 14.6 12 14.6C13.4359 14.6 14.6 13.4359 14.6 12V11.4H14.5304C14.2596 10.2533 13.2294 9.4 12 9.4H11.4Z"
        :fill="color"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'eye-crossed'" class="icon icon--eye-crossed">
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M6.57327 16.5782L4.57574 18.5757L5.42426 19.4243L19.4243 5.42426L18.5757 4.57573L16.4163 6.73515C14.9705 5.85538 13.4988 5.40002 12 5.40002C8.71402 5.40002 5.55846 7.58874 2.52 11.64L2.25 12L2.52 12.36C3.84834 14.1311 5.19906 15.5463 6.57327 16.5782ZM7.43098 15.7205C6.227 14.8429 5.00109 13.6109 3.75425 12C6.61723 8.30107 9.36986 6.60002 12 6.60002C13.1565 6.60002 14.3367 6.92891 15.5395 7.61194L13.3648 9.78663C12.9681 9.54149 12.5006 9.40002 12 9.40002C10.5641 9.40002 9.4 10.5641 9.4 12C9.4 12.5006 9.54147 12.9682 9.78661 13.3649L7.43098 15.7205ZM10.681 12.4705L12.4704 10.681C12.3234 10.6286 12.165 10.6 12 10.6C11.2268 10.6 10.6 11.2268 10.6 12C10.6 12.1651 10.6286 12.3234 10.681 12.4705ZM17.4973 9.02308L18.3472 8.17317C19.4053 9.10602 20.4494 10.2659 21.48 11.64L21.75 12L21.48 12.36C18.4415 16.4113 15.286 18.6 12 18.6C10.8766 18.6 9.76836 18.3442 8.67487 17.8456L9.59117 16.9292C10.405 17.2458 11.2081 17.4 12 17.4C14.6301 17.4 17.3828 15.699 20.2458 12C19.3179 10.8012 18.4016 9.81228 17.4973 9.02308ZM12 14.6C11.9738 14.6 11.9476 14.5996 11.9216 14.5989L14.5988 11.9216C14.5996 11.9476 14.6 11.9738 14.6 12C14.6 13.436 13.4359 14.6 12 14.6Z"
        :fill="color"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'eye-small'" class="icon icon--eye-small">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M8 11.4385C9.63324 11.4385 11.3849 10.3992 13.2435 8.03848C11.3849 5.67775 9.63324 4.63848 8 4.63848C6.36676 4.63848 4.6151 5.67775 2.75654 8.03848C4.6151 10.3992 6.36676 11.4385 8 11.4385ZM1.52 7.67848C3.55846 4.96053 5.71402 3.43848 8 3.43848C10.286 3.43848 12.4415 4.96053 14.48 7.67848L14.75 8.03848L14.48 8.39848C12.4415 11.1164 10.286 12.6385 8 12.6385C5.71402 12.6385 3.55846 11.1164 1.52 8.39848L1.25 8.03848L1.52 7.67848ZM8.73333 8.03848C8.73333 7.63347 8.40501 7.30514 8 7.30514C7.59499 7.30514 7.26667 7.63347 7.26667 8.03848C7.26667 8.44349 7.59499 8.77181 8 8.77181C8.40501 8.77181 8.73333 8.44349 8.73333 8.03848ZM7.4 6.10514V6.20005C6.62597 6.45251 6.06667 7.18017 6.06667 8.03848C6.06667 9.10623 6.93225 9.97181 8 9.97181C9.06775 9.97181 9.93333 9.10623 9.93333 8.03848V7.43848H9.83842C9.58597 6.66445 8.8583 6.10514 8 6.10514H7.4Z"
        :fill="color"
      />
    </svg>
  </span>

  <span
    v-else-if="props.icon === 'eye-crossed-small'"
    class="icon icon--eye-crossed-small"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M4.07981 11.0716L2.57574 12.5757L3.42426 13.4242L13.4243 3.42421L12.5757 2.57568L10.9128 4.23863C9.96585 3.6885 8.99529 3.39998 8 3.39998C5.71402 3.39998 3.55846 4.92203 1.52 7.63998L1.25 7.99998L1.52 8.35998C2.35339 9.47117 3.20636 10.3825 4.07981 11.0716ZM4.93478 10.2166C4.2263 9.67715 3.49997 8.94428 2.75654 7.99998C4.6151 5.63925 6.36676 4.59998 8 4.59998C8.65717 4.59998 9.33351 4.76824 10.0283 5.12314L8.8756 6.27582C8.61272 6.14206 8.31517 6.06664 8 6.06664C6.93225 6.06664 6.06667 6.93223 6.06667 7.99998C6.06667 8.31514 6.14208 8.6127 6.27585 8.87557L4.93478 10.2166ZM7.27745 7.87397L7.87399 7.27743C7.56996 7.33008 7.3301 7.56994 7.27745 7.87397ZM8 12.6C7.19767 12.6 6.41141 12.4125 5.64064 12.0516L6.55817 11.1341C7.04844 11.3136 7.52914 11.4 8 11.4C9.63324 11.4 11.3849 10.3607 13.2435 7.99998C12.6616 7.26089 12.0902 6.65132 11.5296 6.16262L12.3795 5.31276C13.0933 5.94447 13.7933 6.72436 14.48 7.63998L14.75 7.99998L14.48 8.35998C12.4415 11.0779 10.286 12.6 8 12.6ZM8 9.93331C7.92294 9.93331 7.84692 9.9288 7.77221 9.92003L9.92006 7.77219C9.92882 7.8469 9.93333 7.92291 9.93333 7.99998C9.93333 9.06773 9.06775 9.93331 8 9.93331Z"
        :fill="color"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'drag-handle'" class="icon icon--drag-handle">
    <svg
      width="20"
      height="8"
      viewBox="0 0 20 8"
      :stroke="color"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19 4H1M19 1H1M19 7H1"
        stroke-width="1.2"
        stroke-linecap="square"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'dots'" class="icon icon--dots">
    <svg
      class="block mx-auto"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3.5 12.5C3.5 11.6716 4.17157 11 5 11C5.82843 11 6.5 11.6716 6.5 12.5C6.5 13.3284 5.82843 14 5 14C4.17157 14 3.5 13.3284 3.5 12.5Z"
        :fill="color"
      />
      <path
        d="M10.5 12.5C10.5 11.6716 11.1716 11 12 11C12.8284 11 13.5 11.6716 13.5 12.5C13.5 13.3284 12.8284 14 12 14C11.1716 14 10.5 13.3284 10.5 12.5Z"
        :fill="color"
      />
      <path
        d="M19 11C18.1716 11 17.5 11.6716 17.5 12.5C17.5 13.3284 18.1716 14 19 14C19.8284 14 20.5 13.3284 20.5 12.5C20.5 11.6716 19.8284 11 19 11Z"
        :fill="color"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'check'" class="icon icon--check">
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M14 3L6.15789 13L2 8.71429" :stroke="color" stroke-width="1.2" />
    </svg>
  </span>

  <span v-else-if="props.icon === 'error'" class="icon icon--error">
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 13L8 8M8 8L13 3M8 8L13 13M8 8L3 3M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z"
        :stroke="color"
        stroke-width="1.2"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'arrow-down'" class="icon icon--arrow-down">
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M20 8L12 16L4 8"
        :stroke="color"
        stroke-width="1.2"
        stroke-linecap="square"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'arrow-up'" class="icon icon--arrow-up">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="8"
      height="8"
      viewBox="0 0 8 8"
      fill="none"
    >
      <path d="M1 6L4 2L7 6L1 6Z" :fill="color" />
    </svg>
  </span>

  <span v-else-if="props.icon === 'arrow-left'" class="icon icon--arrow-left">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
    >
      <path
        d="M21 12.5385H2M2 12.5385L7 7.53845M2 12.5385L7 17.5385"
        :stroke="color"
        stroke-width="1.2"
        stroke-linecap="square"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'arrow-right'" class="icon icon--arrow-right">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="16"
      viewBox="0 0 14 16"
      fill="none"
    >
      <path d="M14 8L0.499999 15.7942L0.5 0.205771L14 8Z" :fill="color" />
    </svg>
  </span>

  <span
    v-else-if="props.icon === 'arrow-right-large'"
    class="icon icon--arrow-right-large"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="20"
      viewBox="0 0 17 20"
      fill="none"
    >
      <path d="M17 10L0.499999 19.5263L0.5 0.47372L17 10Z" :fill="color" />
    </svg>
  </span>

  <span
    v-else-if="props.icon === 'chevron-left'"
    class="icon icon--chevron-left w-4 h-4"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="8"
      height="8"
      viewBox="0 0 8 8"
      fill="none"
      class="object-cover w-full h-full"
    >
      <path
        d="M5 1L2 4.00001L5 7"
        :stroke="color"
        stroke-width="0.6"
        stroke-linecap="square"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'close'" class="icon icon--close">
    <svg
      width="17"
      height="16"
      viewBox="0 0 17 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.5 12L12.5 4M12.5 12L4.5 4"
        :stroke="color"
        stroke-width="1.2"
        stroke-linecap="square"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'close-large'" class="icon icon--close-large">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M4 20L20 4M20 20L4 4"
        :stroke="color"
        stroke-width="1.2"
        stroke-linecap="square"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'sort'" class="icon icon--sort">
    <svg
      width="8"
      height="9"
      viewBox="0 0 8 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M7 1.5L1 1.5L1 7.5L7 1.5Z" :fill="color" />
    </svg>
  </span>

  <span v-else-if="props.icon === 'active'" class="icon icon--active">
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M3.40073 4.49986C3.40075 3.61622 4.11709 2.8999 5.00073 2.8999H16.0004C16.884 2.8999 17.6004 3.61625 17.6004 4.4999V9.49996H16.4004V4.4999C16.4004 4.27899 16.2213 4.0999 16.0004 4.0999H5.00073C4.77982 4.0999 4.60074 4.27898 4.60073 4.49989L4.60039 18.4999C4.60039 18.7208 4.77947 18.8999 5.00039 18.8999H8.00061V20.0999H5.00039C4.11672 20.0999 3.40037 19.3835 3.40039 18.4999L3.40073 4.49986ZM15.5005 11.0999C12.242 11.0999 9.6005 13.7415 9.6005 16.9999C9.6005 20.2584 12.242 22.8999 15.5005 22.8999C18.759 22.8999 21.4005 20.2584 21.4005 16.9999C21.4005 13.7415 18.759 11.0999 15.5005 11.0999ZM8.4005 16.9999C8.4005 13.0787 11.5793 9.89994 15.5005 9.89994C19.4217 9.89994 22.6005 13.0787 22.6005 16.9999C22.6005 20.9212 19.4217 24.0999 15.5005 24.0999C11.5793 24.0999 8.4005 20.9212 8.4005 16.9999ZM19.0762 13.5757L14.0005 18.6514L11.9248 16.5757L11.0762 17.4242L13.5762 19.9242L14.0005 20.3485L14.4248 19.9242L19.9248 14.4242L19.0762 13.5757Z"
        :fill="color"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'inactive'" class="icon icon--inactive">
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M5.00073 2.8999C4.11709 2.8999 3.40075 3.61622 3.40073 4.49986L3.40039 18.4999C3.40037 19.3835 4.11672 20.0999 5.00039 20.0999H6.50049H9.00061V18.8999H6.50049H5.00039C4.77947 18.8999 4.60039 18.7208 4.60039 18.4999L4.60073 4.49989C4.60074 4.27898 4.77982 4.0999 5.00073 4.0999H16.0004C16.2213 4.0999 16.4004 4.27899 16.4004 4.4999V9.49996H17.6004V4.4999C17.6004 3.61625 16.884 2.8999 16.0004 2.8999H5.00073ZM10.6005 16.9999C10.6005 13.7415 13.242 11.0999 16.5005 11.0999C19.759 11.0999 22.4005 13.7415 22.4005 16.9999C22.4005 20.2584 19.759 22.8999 16.5005 22.8999C13.242 22.8999 10.6005 20.2584 10.6005 16.9999ZM16.5005 9.89994C12.5793 9.89994 9.4005 13.0787 9.4005 16.9999C9.4005 20.9212 12.5793 24.0999 16.5005 24.0999C20.4217 24.0999 23.6005 20.9212 23.6005 16.9999C23.6005 13.0787 20.4217 9.89994 16.5005 9.89994ZM16.5005 16.1514L13.4248 13.0757L12.5762 13.9242L15.652 16.9999L12.5762 20.0757L13.4248 20.9242L16.5005 17.8485L19.5762 20.9242L20.4248 20.0757L17.349 16.9999L20.4248 13.9242L19.5762 13.0757L16.5005 16.1514Z"
        :fill="color"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'pen'" class="icon icon--pen">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="16"
      viewBox="0 0 17 16"
      fill="none"
    >
      <path
        d="M11.8217 3L3.5 11.3217V13H5.17832L13.5 4.67832L11.8217 3Z"
        :stroke="color"
        stroke-width="1.2"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'filter'" class="icon icon--filter">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M15 8H3M15 8V6M15 8V10M20.5 8H18M8 16H3M8 16V14M8 16V18M20.5 16H11"
        :stroke="color"
        stroke-width="1.2"
        stroke-linecap="square"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'refresh'" class="icon icon--refresh">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M12 21C16.9706 21 21 16.9706 21 12C21 8.51707 19.0216 5.49623 16.1272 4M12 3C7.02944 3 3 7.02944 3 12C3 15.5337 5.03656 18.5918 8 20.0645M16.1272 4L16.5 7.5M16.1272 4L20 4.5M8 20.0645L7.5 16.5M8 20.0645L4 19.5"
        :stroke="color"
        stroke-width="1.2"
        stroke-linecap="square"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'outer-link'" class="icon icon--outer-link">
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.99219 8.00784L12.9999 3M12.9999 3H10.0046M12.9999 3V6.01085M7.00152 4H4.33124C3.59602 4 3 4.59603 3 5.33126V11.6687C3 12.404 3.59602 13 4.33124 13H10.6546C11.3898 13 11.9858 12.404 11.9858 11.6687V8.99062"
        :stroke="color"
        stroke-width="1.2"
        stroke-linecap="square"
      />
    </svg>
  </span>

  <span v-else-if="props.icon === 'download'" class="icon icon--download">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M12.5 3L12.5 14.5M12.5 14.5L9 11M12.5 14.5L16 11"
        :stroke="color"
        stroke-width="1.2"
        stroke-linecap="square"
      />
      <path
        d="M4 16L4 18C4 19.1046 4.89543 20 6 20L19 20C20.1046 20 21 19.1046 21 18L21 16"
        :stroke="color"
        stroke-width="1.2"
        stroke-linecap="square"
      />
    </svg>
  </span>

  <span
    v-else-if="props.icon === 'check-rounded'"
    class="icon icon--check-rounded"
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M1.60039 7.50039C1.60039 4.24191 4.24191 1.60039 7.50039 1.60039C10.7589 1.60039 13.4004 4.24191 13.4004 7.50039C13.4004 10.7589 10.7589 13.4004 7.50039 13.4004C4.24191 13.4004 1.60039 10.7589 1.60039 7.50039ZM7.50039 0.400391C3.57917 0.400391 0.400391 3.57917 0.400391 7.50039C0.400391 11.4216 3.57917 14.6004 7.50039 14.6004C11.4216 14.6004 14.6004 11.4216 14.6004 7.50039C14.6004 3.57917 11.4216 0.400391 7.50039 0.400391ZM11.0761 4.07613L6.00039 9.15186L3.92465 7.07613L3.07613 7.92465L5.57613 10.4247L6.00039 10.8489L6.42465 10.4247L11.9247 4.92465L11.0761 4.07613Z"
        :fill="color"
      />
    </svg>
  </span>

  <span
    v-else-if="props.icon === 'close-rounded'"
    class="icon icon--close-rounded"
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M7.50039 1.60039C4.24191 1.60039 1.60039 4.24191 1.60039 7.50039C1.60039 10.7589 4.24191 13.4004 7.50039 13.4004C10.7589 13.4004 13.4004 10.7589 13.4004 7.50039C13.4004 4.24191 10.7589 1.60039 7.50039 1.60039ZM0.400391 7.50039C0.400391 3.57917 3.57917 0.400391 7.50039 0.400391C11.4216 0.400391 14.6004 3.57917 14.6004 7.50039C14.6004 11.4216 11.4216 14.6004 7.50039 14.6004C3.57917 14.6004 0.400391 11.4216 0.400391 7.50039ZM7.50039 6.65186L4.42465 3.57613L3.57613 4.42465L6.65186 7.50039L3.57613 10.5761L4.42465 11.4247L7.50039 8.34892L10.5761 11.4247L11.4247 10.5761L8.34892 7.50039L11.4247 4.42465L10.5761 3.57613L7.50039 6.65186Z"
        :fill="color"
      />
    </svg>
  </span>
</template>

<style scoped>
  .icon {
    display: block;
  }
  /* Plus */
  span.icon--plus {
    position: relative;
    width: 19px;
    height: 1px;
  }
  span.icon--plus > span {
    background-color: inherit;
    position: absolute;
    width: 1px;
    height: 19px;
    top: -9px;
    left: 9px;
  }
  /* Minus */
  span.icon--minus {
    position: relative;
    width: 13px;
    height: 1px;
  }
  span.icon--plus-small {
    background-color: black;
    width: 13px;
  }
  span.icon--plus-small > span {
    height: 13px;
    top: -6px;
    left: 6px;
  }
  span.icon--plus-medium {
    background-color: black;
    width: 11px;
    margin: 8px 3px 7px 2px;
  }
  span.icon--plus-medium > span {
    height: 11px;
    top: -5px;
    left: 5px;
  }
</style>
